import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Divider
} from '@mui/material';
import { Google, Microsoft, LinkedIn } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { isSafeReturnTo, takeReturnTo } from '../utils/returnTo';
import LoycoBrandLink, { LOYCO_BRAND_BG } from './LoycoBrandLink';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (user && !loading) {
      const fromQuery = searchParams.get('returnTo');
      const fromStore = takeReturnTo();
      const returnTo = isSafeReturnTo(fromQuery) ? fromQuery : fromStore;
      navigate(isSafeReturnTo(returnTo) ? returnTo : '/', { replace: true });
    }
  }, [user, loading, navigate, searchParams]);

  const error = searchParams.get('error');
  const errorMessages: Record<string, string> = {
    google_failed: 'Google authentication failed. Please try again.',
    microsoft_failed: 'Microsoft authentication failed. Please try again.',
    linkedin_failed: 'LinkedIn authentication failed. Please try again.',
    no_user: 'Authentication failed. No user information received.',
    callback_error: 'Authentication callback error. Please try again.'
  };

  const { login } = useAuth();

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={0} sx={{ p: 5, borderRadius: 3, border: '1px solid #E0E0E0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <LoycoBrandLink to="/login" logoHeight={50} />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 700, color: '#1A1A1A', mb: 1 }}>
          Sign in
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4, color: '#666666' }}>
          Sign in to access the API portal
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error === 'not_whitelisted' ? (
              <>
                The default access to this portal is limited.{' '}
                <a
                  href="/docs/developer-portal-access.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: LOYCO_BRAND_BG }}
                >
                  Access guide (PDF)
                </a>
                . For extended access, email{' '}
                <a href="mailto:support@loyco.no" style={{ color: LOYCO_BRAND_BG }}>
                  support@loyco.no
                </a>
                .
              </>
            ) : (
              errorMessages[error] || 'An error occurred during authentication'
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<Google />}
            onClick={() => login('google')}
            sx={{
              py: 1.5,
              borderColor: '#E0E0E0',
              color: '#1A1A1A',
              '&:hover': {
                borderColor: LOYCO_BRAND_BG,
                backgroundColor: 'rgba(17, 61, 81, 0.04)',
              }
            }}
          >
            Sign in with Google
          </Button>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<Microsoft />}
            onClick={() => login('microsoft')}
            sx={{
              py: 1.5,
              borderColor: '#E0E0E0',
              color: '#1A1A1A',
              '&:hover': {
                borderColor: LOYCO_BRAND_BG,
                backgroundColor: 'rgba(17, 61, 81, 0.04)',
              }
            }}
          >
            Sign in with Microsoft
          </Button>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<LinkedIn />}
            onClick={() => login('linkedin')}
            sx={{
              py: 1.5,
              borderColor: '#E0E0E0',
              color: '#1A1A1A',
              '&:hover': {
                borderColor: LOYCO_BRAND_BG,
                backgroundColor: 'rgba(17, 61, 81, 0.04)',
              }
            }}
          >
            Sign in with LinkedIn
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" align="center" sx={{ color: '#666666' }}>
          The default access to this portal is limited.{' '}
          <a
            href="/docs/developer-portal-access.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: LOYCO_BRAND_BG }}
          >
            Access guide (PDF)
          </a>
          {' · '}
          <a
            href="/docs/loyco-sandbox-exampleapp.zip"
            style={{ color: LOYCO_BRAND_BG }}
          >
            Example app (zip)
          </a>
          . For extended access, email{' '}
          <a href="mailto:support@loyco.no" style={{ color: LOYCO_BRAND_BG }}>
            support@loyco.no
          </a>
          .
        </Typography>
      </Paper>
    </Container>
  );
}
